"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect } from "react";

import { LocaleLink, useLocaleHref, useTranslator } from "~/i18n/provider";
import { categoryHref } from "~/lib/storefront/category-href";
import { clearAllFilters } from "~/lib/storefront/filter-params";

/**
 * P2-11 · Filter-empty state: the active filters exclude everything. Unlike the
 * category-empty state (P2-09), this IS a filter problem, so it leads with
 * "Clear all filters" and keeps the shopper's other options in reach
 * (P2-DB02 §04).
 */
export function PlpFilterEmptyState({
  suggestions,
}: {
  suggestions?: { name: string; slug: string }[];
}) {
  const t = useTranslator();
  const router = useRouter();
  const pathname = usePathname();
  const params = useSearchParams();
  const localeHref = useLocaleHref();

  useEffect(() => {}, []);

  const clearAll = () => {
    const next = clearAllFilters(params.toString());
    router.push(localeHref(next ? `${pathname}?${next}` : pathname), {
      scroll: false,
    });
  };

  return (
    <div className="flex flex-col items-center gap-4 rounded-xl border border-border bg-surface-2 px-6 py-16 text-center">
      <h2 className="font-heading text-xl font-semibold text-fg">
        {t("browse.noMatch")}
      </h2>
      <p className="max-w-md text-sm text-fg-muted">
        {t("browse.noMatchHint")}
      </p>
      <button
        type="button"
        onClick={clearAll}
        className="rounded-[var(--btn-radius)] bg-primary px-4 py-2 text-sm text-primary-fg [font-weight:var(--btn-weight)] hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
      >
        {t("browse.clearAllFilters")}
      </button>
      {suggestions && suggestions.length > 0 && (
        <ul className="flex flex-wrap justify-center gap-2 pt-2">
          {suggestions.map((s) => (
            <li key={s.slug}>
              <LocaleLink
                href={categoryHref(s.slug)}
                className="inline-block rounded-full border border-border px-3 py-1.5 text-sm text-fg hover:bg-surface"
              >
                {s.name}
              </LocaleLink>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
